import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  type NestUseCase,
  type UploadProgress,
  UploadStatus,
} from "src/api/interfaces/nest";
import type { NestTestResult } from "src/api/interfaces/nest/history";
import { useNestUseCaseFetcher } from "./useNestUseCaseFetcher";
import { useDocumentTypePreference } from "./useDocumentTypePreference";
import client from "src/api/client";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { useNest } from "src/components/Contexts/NestContext";
import { AUTO_CLOSE_SINGLE_SUCCESS_MS } from "../constants";

interface NestContentState {
  selectedUploadUseCase: NestUseCase | null;
  uploadFiles: File[];
  uploadUseCaseId: string;
  uploadDocumentTypeId: string;
  completedUploads: UploadProgress[];
  useCaseHistory: NestUseCase | null;
  uploadResults: Map<string, NestTestResult>;
  selectedUploadTest: NestTestResult | null;
}

const INITIAL_STATE: NestContentState = {
  selectedUploadUseCase: null,
  uploadFiles: [],
  uploadUseCaseId: "",
  uploadDocumentTypeId: "",
  completedUploads: [],
  useCaseHistory: null,
  uploadResults: new Map(),
  selectedUploadTest: null,
};

const HTTP_SERVER_ERROR_MIN = 500;

type Action =
  | { type: "CLOSED"; overrides?: Partial<NestContentState> }
  | {
      type: "USE_CASE_UPLOAD_REQUESTED";
      useCase: NestUseCase;
      documentTypeId: string;
    }
  | {
      type: "NEW_TEST_REQUESTED";
      useCase: NestUseCase;
      documentTypeId: string;
    }
  | { type: "UPLOAD_STARTED"; files: File[]; useCaseId: string }
  | {
      type: "UPLOAD_FINISHED";
      results: UploadProgress[];
      singleSuccessTest: NestTestResult | null;
    }
  | { type: "UPLOAD_RESULT_DISMISSED" }
  | { type: "UPLOAD_RETRY_REQUESTED"; fileName?: string }
  | { type: "UPLOAD_RESULT_OPENED"; fileName: string }
  | { type: "STATUS_TEST_LOADED"; test: NestTestResult }
  | { type: "HISTORY_OPENED"; useCase: NestUseCase };

function reducer(state: NestContentState, action: Action): NestContentState {
  switch (action.type) {
    case "CLOSED":
      return {
        ...INITIAL_STATE,
        uploadResults: state.uploadResults,
        ...action.overrides,
      };

    case "USE_CASE_UPLOAD_REQUESTED":
      return {
        ...state,
        selectedUploadUseCase: action.useCase,
        uploadDocumentTypeId: action.documentTypeId,
        uploadUseCaseId: "",
        completedUploads: [],
        useCaseHistory: null,
        selectedUploadTest: null,
      };

    case "NEW_TEST_REQUESTED":
      return {
        ...state,
        selectedUploadUseCase: action.useCase,
        uploadDocumentTypeId: action.documentTypeId,
        uploadFiles: [],
        uploadUseCaseId: "",
        completedUploads: [],
        useCaseHistory: null,
        selectedUploadTest: null,
      };

    case "UPLOAD_STARTED":
      return {
        ...state,
        uploadFiles: action.files,
        uploadUseCaseId: action.useCaseId,
        selectedUploadUseCase: null,
        completedUploads: [],
      };

    case "UPLOAD_FINISHED": {
      const nextResults = new Map(state.uploadResults);
      for (const result of action.results) {
        if (result.testResult)
          nextResults.set(result.fileName, result.testResult);
      }
      return {
        ...state,
        selectedUploadUseCase: null,
        uploadFiles: [],
        completedUploads: action.results,
        uploadResults: nextResults,
        selectedUploadTest: action.singleSuccessTest,
      };
    }

    case "UPLOAD_RESULT_DISMISSED":
      return { ...state, completedUploads: [] };

    case "UPLOAD_RETRY_REQUESTED": {
      const toRetry = action.fileName
        ? state.completedUploads.filter((u) => u.fileName === action.fileName)
        : state.completedUploads.filter(
            (u) =>
              u.status === UploadStatus.ERROR &&
              u.statusCode !== undefined &&
              u.statusCode >= HTTP_SERVER_ERROR_MIN
          );
      return {
        ...state,
        uploadFiles: toRetry.map((u) => u.file),
        selectedUploadUseCase: null,
        completedUploads: [],
      };
    }

    case "UPLOAD_RESULT_OPENED": {
      const testResult = state.uploadResults.get(action.fileName);
      if (!testResult) return state;
      return { ...state, selectedUploadTest: testResult };
    }

    case "STATUS_TEST_LOADED":
      return {
        ...INITIAL_STATE,
        uploadResults: state.uploadResults,
        selectedUploadTest: action.test,
      };

    case "HISTORY_OPENED":
      return {
        ...state,
        useCaseHistory: action.useCase,
        selectedUploadUseCase: null,
        uploadUseCaseId: "",
        uploadDocumentTypeId: "",
        completedUploads: [],
        selectedUploadTest: null,
      };
  }
}

export function useNestContent() {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const {
    data: useCases,
    isLoading,
    silentRefetch: refetchUseCases,
  } = useNestUseCaseFetcher();
  const { getDocumentType, setDocumentType } = useDocumentTypePreference();
  const { selectedCountry } = useCountrySelection();
  const { partnerCode } = useNest();
  const statusClickAbortRef = useRef<AbortController | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const abortPendingStatusClick = useCallback(() => {
    statusClickAbortRef.current?.abort();
    statusClickAbortRef.current = null;
  }, []);

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current !== null) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      statusClickAbortRef.current?.abort();
      statusClickAbortRef.current = null;
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, []);

  const handleClose = useCallback(
    (overrides?: Partial<NestContentState>) => {
      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({ type: "CLOSED", overrides });
    },
    [abortPendingStatusClick, clearAutoCloseTimer]
  );

  const handleUseCaseUploadRequested = useCallback(
    (useCase: NestUseCase, documentTypeId: string) => {
      setDocumentType(useCase.id, documentTypeId);
      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({
        type: "USE_CASE_UPLOAD_REQUESTED",
        useCase,
        documentTypeId,
      });
    },
    [abortPendingStatusClick, clearAutoCloseTimer, setDocumentType]
  );

  const handleNewTestRequested = useCallback(
    (useCaseId: string) => {
      const useCase = useCases.find((u) => u.id === useCaseId);
      if (!useCase) return;
      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({
        type: "NEW_TEST_REQUESTED",
        useCase,
        documentTypeId: getDocumentType(useCase).id,
      });
    },
    [abortPendingStatusClick, clearAutoCloseTimer, getDocumentType, useCases]
  );

  const handleUploadStarted = useCallback(
    (files: File[], useCaseId: string) => {
      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({ type: "UPLOAD_STARTED", files, useCaseId });
    },
    [abortPendingStatusClick, clearAutoCloseTimer]
  );

  const handleUploadFinished = useCallback(
    (results: UploadProgress[]) => {
      const singleSuccess =
        results.length === 1 && results[0].testResult
          ? results[0].testResult
          : null;

      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({
        type: "UPLOAD_FINISHED",
        results,
        singleSuccessTest: singleSuccess,
      });

      if (singleSuccess) {
        autoCloseTimerRef.current = setTimeout(() => {
          autoCloseTimerRef.current = null;
          dispatch({ type: "UPLOAD_RESULT_DISMISSED" });
        }, AUTO_CLOSE_SINGLE_SUCCESS_MS);
      }

      refetchUseCases();
    },
    [abortPendingStatusClick, clearAutoCloseTimer, refetchUseCases]
  );

  const handleUploadRetryRequested = useCallback(
    (fileName?: string) => {
      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({ type: "UPLOAD_RETRY_REQUESTED", fileName });
    },
    [abortPendingStatusClick, clearAutoCloseTimer]
  );

  const handleUploadResultOpened = useCallback((fileName: string) => {
    dispatch({ type: "UPLOAD_RESULT_OPENED", fileName });
  }, []);

  const handleStatusClick = useCallback(
    async (useCase: NestUseCase) => {
      abortPendingStatusClick();
      const controller = new AbortController();
      statusClickAbortRef.current = controller;

      try {
        const response = await client.getNestHistory(
          {
            useCaseId: useCase.id,
            runId: useCase.lastRun.id,
            countryCode: selectedCountry?.countryCode,
            partnerCode: partnerCode ?? undefined,
            limit: 1,
          },
          controller.signal
        );
        if (controller.signal.aborted) return;
        const test = response.tests[0];
        if (test) {
          dispatch({ type: "STATUS_TEST_LOADED", test });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        throw error;
      } finally {
        if (statusClickAbortRef.current === controller) {
          statusClickAbortRef.current = null;
        }
      }
    },
    [abortPendingStatusClick, partnerCode, selectedCountry?.countryCode]
  );

  const handleHistoryOpened = useCallback(
    (useCase: NestUseCase) => {
      abortPendingStatusClick();
      clearAutoCloseTimer();
      dispatch({ type: "HISTORY_OPENED", useCase });
    },
    [abortPendingStatusClick, clearAutoCloseTimer]
  );

  return {
    useCases,
    isLoading,
    selectedUploadUseCase: state.selectedUploadUseCase,
    uploadFiles: state.uploadFiles,
    uploadUseCaseId: state.uploadUseCaseId,
    uploadDocumentTypeId: state.uploadDocumentTypeId,
    completedUploads: state.completedUploads,
    useCaseHistory: state.useCaseHistory,
    selectedUploadTest: state.selectedUploadTest,
    handleClose,
    handleUseCaseUploadRequested,
    handleNewTestRequested,
    handleUploadStarted,
    handleUploadFinished,
    handleUploadRetryRequested,
    handleUploadResultOpened,
    handleStatusClick,
    handleHistoryOpened,
  };
}
